import { FrameworkTemplate, SectionTemplate, SubsectionTemplate, ItemTemplate } from '../models/types';
import { DEFAULT_FRAMEWORK_TEMPLATE } from '../models/defaultTemplate';

/**
 * Service for managing framework templates
 * Currently uses localStorage but designed to be easily replaced with a proper backend
 */
export class TemplateService {
  private readonly TEMPLATES_KEY = 'requirements_tracker_templates';

  /**
   * Initialize templates in storage
   */
  public initializeIfEmpty(): void {
    const templates = this.getAllTemplates();
    
    if (templates.length === 0) {
      // Store the default template
      this.saveTemplate(DEFAULT_FRAMEWORK_TEMPLATE);
    }
  }

  /**
   * Get all templates
   * @returns Array of all templates
   */
  public getAllTemplates(): FrameworkTemplate[] {
    const templatesJson = localStorage.getItem(this.TEMPLATES_KEY);
    if (!templatesJson) return [];
    
    try {
      return JSON.parse(templatesJson);
    } catch (e) {
      console.error('Failed to parse templates from storage', e);
      return [];
    }
  }

  /**
   * Get a template by ID
   * @param id The template ID to retrieve
   * @returns The template if found, null otherwise
   */
  public getTemplate(id: string): FrameworkTemplate | null {
    const templates = this.getAllTemplates();
    const template = templates.find(t => t.id === id) || null;
    return template;
  }

  /**
   * Save a template to storage
   * @param template The template to save
   * @returns The saved template
   */
  public saveTemplate(template: FrameworkTemplate): FrameworkTemplate {
    // Get existing templates
    const templates = this.getAllTemplates();
    
    // Find and replace if exists, otherwise add
    const index = templates.findIndex(t => t.id === template.id);
    if (index >= 0) {
      templates[index] = template;
    } else {
      templates.push(template);
    }
    
    // Save back to storage
    localStorage.setItem(this.TEMPLATES_KEY, JSON.stringify(templates));
    
    return template;
  }

  /**
   * Delete a template
   * @param id The ID of the template to delete
   * @returns true if deleted, false if not found
   */
  public deleteTemplate(id: string): boolean {
    const templates = this.getAllTemplates();
    const initialLength = templates.length;
    
    const filteredTemplates = templates.filter(t => t.id !== id);
    localStorage.setItem(this.TEMPLATES_KEY, JSON.stringify(filteredTemplates));
    
    return filteredTemplates.length < initialLength;
  }

  /**
   * Create a new template
   * @param name Template name
   * @param description Template description
   * @returns The newly created template
   */
  public createNewTemplate(name: string, description: string): FrameworkTemplate {
    const template: FrameworkTemplate = {
      id: crypto.randomUUID(),
      name,
      description,
      sections: []
    };
    
    this.saveTemplate(template);
    return template;
  }

  /**
   * Clone an existing template
   * @param id ID of the template to clone
   * @param newName New name for the cloned template
   * @returns The cloned template
   */
  public cloneTemplate(id: string, newName: string): FrameworkTemplate | null {
    const template = this.getTemplate(id);
    if (!template) return null;
    
    const clonedTemplate: FrameworkTemplate = {
      ...template,
      id: crypto.randomUUID(),
      name: newName || `${template.name} (Copy)`
    };
    
    this.saveTemplate(clonedTemplate);
    return clonedTemplate;
  }

  /**
   * Add a section to a template
   * @param templateId ID of the template
   * @param section The section to add
   * @returns The updated template
   */
  public addSection(templateId: string, section: SectionTemplate): FrameworkTemplate | null {
    const template = this.getTemplate(templateId);
    if (!template) return null;
    
    template.sections.push(section);
    return this.saveTemplate(template);
  }

  /**
   * Update a section in a template
   * @param templateId ID of the template
   * @param sectionIndex Index of the section to update
   * @param section The updated section
   * @returns The updated template
   */
  public updateSection(
    templateId: string,
    sectionIndex: number,
    section: SectionTemplate
  ): FrameworkTemplate | null {
    const template = this.getTemplate(templateId);
    if (!template || sectionIndex < 0 || sectionIndex >= template.sections.length) {
      return null;
    }
    
    template.sections[sectionIndex] = section;
    return this.saveTemplate(template);
  }

  /**
   * Remove a section from a template
   * @param templateId ID of the template
   * @param sectionIndex Index of the section to remove
   * @returns The updated template
   */
  public removeSection(templateId: string, sectionIndex: number): FrameworkTemplate | null {
    const template = this.getTemplate(templateId);
    if (!template || sectionIndex < 0 || sectionIndex >= template.sections.length) {
      return null;
    }
    
    template.sections.splice(sectionIndex, 1);
    return this.saveTemplate(template);
  }

  /**
   * Add a subsection to a section
   * @param templateId ID of the template
   * @param sectionIndex Index of the section
   * @param subsection The subsection to add
   * @returns The updated template
   */
  public addSubsection(
    templateId: string,
    sectionIndex: number,
    subsection: SubsectionTemplate
  ): FrameworkTemplate | null {
    const template = this.getTemplate(templateId);
    if (!template || sectionIndex < 0 || sectionIndex >= template.sections.length) {
      return null;
    }
    
    template.sections[sectionIndex].subsections.push(subsection);
    return this.saveTemplate(template);
  }

  /**
   * Update a subsection in a template
   * @param templateId ID of the template
   * @param sectionIndex Index of the section
   * @param subsectionIndex Index of the subsection to update
   * @param subsection The updated subsection
   * @returns The updated template
   */
  public updateSubsection(
    templateId: string,
    sectionIndex: number,
    subsectionIndex: number,
    subsection: SubsectionTemplate
  ): FrameworkTemplate | null {
    const template = this.getTemplate(templateId);
    if (!template 
        || sectionIndex < 0 
        || sectionIndex >= template.sections.length
        || subsectionIndex < 0
        || subsectionIndex >= template.sections[sectionIndex].subsections.length) {
      return null;
    }
    
    template.sections[sectionIndex].subsections[subsectionIndex] = subsection;
    return this.saveTemplate(template);
  }

  /**
   * Remove a subsection from a template
   * @param templateId ID of the template
   * @param sectionIndex Index of the section
   * @param subsectionIndex Index of the subsection to remove
   * @returns The updated template
   */
  public removeSubsection(
    templateId: string,
    sectionIndex: number,
    subsectionIndex: number
  ): FrameworkTemplate | null {
    const template = this.getTemplate(templateId);
    if (!template 
        || sectionIndex < 0 
        || sectionIndex >= template.sections.length
        || subsectionIndex < 0
        || subsectionIndex >= template.sections[sectionIndex].subsections.length) {
      return null;
    }
    
    template.sections[sectionIndex].subsections.splice(subsectionIndex, 1);
    return this.saveTemplate(template);
  }

  /**
   * Add an item to a subsection
   * @param templateId ID of the template
   * @param sectionIndex Index of the section
   * @param subsectionIndex Index of the subsection
   * @param item The item to add
   * @returns The updated template
   */
  public addItem(
    templateId: string,
    sectionIndex: number,
    subsectionIndex: number,
    item: ItemTemplate
  ): FrameworkTemplate | null {
    const template = this.getTemplate(templateId);
    if (!template 
        || sectionIndex < 0 
        || sectionIndex >= template.sections.length
        || subsectionIndex < 0
        || subsectionIndex >= template.sections[sectionIndex].subsections.length) {
      return null;
    }
    
    template.sections[sectionIndex].subsections[subsectionIndex].items.push(item);
    return this.saveTemplate(template);
  }

  /**
   * Update an item in a template
   * @param templateId ID of the template
   * @param sectionIndex Index of the section
   * @param subsectionIndex Index of the subsection
   * @param itemIndex Index of the item to update
   * @param item The updated item
   * @returns The updated template
   */
  public updateItem(
    templateId: string,
    sectionIndex: number,
    subsectionIndex: number,
    itemIndex: number,
    item: ItemTemplate
  ): FrameworkTemplate | null {
    const template = this.getTemplate(templateId);
    if (!template 
        || sectionIndex < 0 
        || sectionIndex >= template.sections.length
        || subsectionIndex < 0
        || subsectionIndex >= template.sections[sectionIndex].subsections.length
        || itemIndex < 0
        || itemIndex >= template.sections[sectionIndex].subsections[subsectionIndex].items.length) {
      return null;
    }
    
    template.sections[sectionIndex].subsections[subsectionIndex].items[itemIndex] = item;
    return this.saveTemplate(template);
  }

  /**
   * Remove an item from a template
   * @param templateId ID of the template
   * @param sectionIndex Index of the section
   * @param subsectionIndex Index of the subsection
   * @param itemIndex Index of the item to remove
   * @returns The updated template
   */
  public removeItem(
    templateId: string,
    sectionIndex: number,
    subsectionIndex: number,
    itemIndex: number
  ): FrameworkTemplate | null {
    const template = this.getTemplate(templateId);
    if (!template 
        || sectionIndex < 0 
        || sectionIndex >= template.sections.length
        || subsectionIndex < 0
        || subsectionIndex >= template.sections[sectionIndex].subsections.length
        || itemIndex < 0
        || itemIndex >= template.sections[sectionIndex].subsections[subsectionIndex].items.length) {
      return null;
    }
    
    template.sections[sectionIndex].subsections[subsectionIndex].items.splice(itemIndex, 1);
    return this.saveTemplate(template);
  }
}

export const templateService = new TemplateService();
